import { all, call, getContext, put, select, takeLatest } from 'redux-saga/effects';

import type { ForgotPasswordDraft } from '../domain/forgot-password.model';
import { validateDraft } from '../domain/forgot-password.validation';
import { toForgotPasswordError } from '../domain/forgot-password-error';
import type { ForgotPasswordGateway } from '../infrastructure/forgot-password.gateway';
import { submitRequested } from './forgot-password.actions';
import { selectDraft } from './forgot-password.selectors';
import { forgotPasswordActions } from './forgot-password.slice';

/**
 * WORKER: request a reset link for the drafted email. Validates up front (a
 * `validation` error short-circuits before any I/O), then all I/O goes through
 * the injected gateway (read from the saga `context`; never the api client), so
 * tests drive the success / api-error / network-error branches with an in-memory
 * gateway. `takeLatest` means a fresh submit supersedes any in-flight request.
 */
function* submitWorker() {
  const draft = (yield select(selectDraft)) as ForgotPasswordDraft;

  const errors = validateDraft(draft);
  if (Object.keys(errors).length > 0) {
    yield put(forgotPasswordActions.submitFailed({ kind: 'validation', fields: errors }));
    return;
  }

  yield put(forgotPasswordActions.submitStarted());
  const gateway: ForgotPasswordGateway = yield getContext('forgotPasswordGateway');
  try {
    yield call(gateway.requestPasswordReset, draft.email.trim());
    yield put(forgotPasswordActions.submitSucceeded());
  } catch (e) {
    yield put(forgotPasswordActions.submitFailed(toForgotPasswordError(e)));
  }
}

/** WATCHERS. */
export function* forgotPasswordSaga() {
  yield all([takeLatest(submitRequested.type, submitWorker)]);
}
